import { useState, type FormEvent } from "react";
import { useAuth } from "../lib/auth";
import { Button } from "@/components/ui/Button";
import { Field, TextInput } from "@/components/ui/Field";

/**
 * Cinematic sign-in. The page is the only un-authenticated surface
 * in the app, so it gets editorial treatment:
 *   - large serif wordmark (Fraunces)
 *   - layered radial gradients sitting on the body atmosphere wash
 *   - left column reads like a film still caption
 *   - right column is the sign-in card
 *
 * On narrow viewports it collapses to a single centered column.
 */
export function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative h-full overflow-hidden">
      {/* Atmospheric backdrop — single huge brass gradient anchored
          off-screen + a faint film-grain noise texture. */}
      <BackdropAtmosphere />

      <div className="relative grid h-full grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
        {/* Left: editorial caption column. Hidden on mobile. */}
        <aside className="hidden flex-col justify-between p-12 lg:flex">
          <div className="flex items-center gap-3">
            <Mark />
            <span className="font-display text-xl tracking-display-tight">
              arr-in-one
            </span>
          </div>

          <div className="max-w-md space-y-6">
            <p className="eyebrow">A unified library</p>
            <h1 className="font-display text-5xl font-light italic leading-[0.95] tracking-display-tighter text-foreground/90">
              Movies, music,
              <br />
              and television
              <br />
              <span className="text-accent">under one roof.</span>
            </h1>
            <p className="max-w-sm text-sm leading-relaxed text-foreground/55">
              One process. One database. One interface for what used to take
              four. Sign in to your archive.
            </p>
          </div>

          <p className="text-xs text-foreground/40">v0.1.0 · self-hosted</p>
        </aside>

        {/* Right: sign-in card. */}
        <main className="grid place-items-center px-6 py-10 lg:px-12">
          <form
            onSubmit={onSubmit}
            className="w-full max-w-sm space-y-6 rounded-lg border border-line bg-surface/70 p-8 shadow-elev backdrop-blur-md animate-rise-in"
            noValidate
          >
            <div className="space-y-1.5">
              <div className="eyebrow">Sign in</div>
              <h2 className="font-display text-2xl font-semibold leading-tight tracking-display-tight">
                Welcome back
              </h2>
              <p className="text-sm text-foreground/60">
                Enter your credentials to continue.
              </p>
            </div>

            <div className="space-y-4">
              <Field label="Username">
                <TextInput
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                />
              </Field>

              <Field label="Password">
                <TextInput
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </Field>
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-md border border-bad/40 bg-bad/10 px-3 py-2 text-sm text-bad"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              loading={submitting}
              className="w-full"
            >
              {submitting ? "Signing in" : "Sign in"}
            </Button>
          </form>
        </main>
      </div>
    </div>
  );
}

function Mark() {
  // Geometric brass mark — a circle bisected by a serif terminal stroke.
  // Pure SVG so it inherits theme colors automatically.
  return (
    <svg viewBox="0 0 32 32" className="h-8 w-8" aria-hidden>
      <circle
        cx={16}
        cy={16}
        r={14}
        fill="none"
        stroke="hsl(38 56% 62%)"
        strokeWidth={1.5}
      />
      <path
        d="M11 22V11h7c2.5 0 4 1.5 4 4s-1.5 4-4 4h-7"
        fill="none"
        stroke="hsl(38 56% 62%)"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 19l5 4"
        fill="none"
        stroke="hsl(38 56% 62%)"
        strokeWidth={1.8}
        strokeLinecap="round"
      />
    </svg>
  );
}

function BackdropAtmosphere() {
  return (
    <>
      {/* Big radial brass wash from the bottom-left. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(70% 60% at 25% 90%, hsl(38 56% 62% / 0.18), transparent 60%), radial-gradient(40% 50% at 80% 10%, hsl(8 50% 40% / 0.10), transparent 60%)",
        }}
      />
      {/* Faint horizontal scan-lines for film/archival feel. Very subtle. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, hsl(36 18% 92%) 0 1px, transparent 1px 3px)",
        }}
      />
    </>
  );
}
